import { useMemo, useState } from "react";
import { arrayOf, shape, string } from "prop-types";
import Select from "../../../../Common/js/components/Select/Select.jsx";
import HighlightText from "../../../../Common/js/components/HighlightText/HighlightText.jsx";
import * as R from "ramda";
import cx from "classnames";

const tagShape = shape({ value: string });

const propTypes = {
  configuration: shape({
    titleLabel: string,
    authorLabel: string,
    yearLabel: string,
    fileTypeLabel: string,
    searchPlaceholder: string,
    tagFacetLabel: string,
    typeFacetLabel: string,
  }),
  resources: arrayOf(
    shape({
      title: string.isRequired,
      author: string.isRequired,
      url: string.isRequired,
      type: string.isRequired,
      tags: arrayOf(tagShape),
    }),
  ).isRequired,
  tags: arrayOf(tagShape).isRequired,
  types: arrayOf(
    shape({
      type: string,
      fileIconUrl: string,
      linkIconUrl: string,
    }),
  ).isRequired,
};

const defaultProps = {
  configuration: {
    titleLabel: "Title",
    authorLabel: "Author",
    yearLabel: "Year",
    fileTypeLabel: "Filetype",
    searchPlaceholder: "Search",
    tagFacetLabel: "Tag",
    typeFacetLabel: "Doc Type",
  },
};

const Resources = (props) => {
  const {
    configuration: {
      titleLabel,
      authorLabel,
      yearLabel,
      fileTypeLabel,
      searchPlaceholder,
      tagFacetLabel,
      typeFacetLabel,
    },
    resources,
    tags,
    types,
  } = props;

  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState();
  const [typeFilter, setTypeFilter] = useState();
  const [sortField, setSortField] = useState("author");
  const [sortAsc, setSortAsc] = useState(true);

  const sortOptions = ["title", "author", "year"];

  const handleSort = (field) => {
    sortField === field ? setSortAsc(!sortAsc) : null;
    setSortField(field);
  };

  const results = R.compose(
    R.sort(
      sortAsc ? R.ascend(R.prop(sortField)) : R.descend(R.prop(sortField)),
    ),
    R.filter(
      R.allPass([
        // filter by the title, author, year fields
        R.anyPass(
          sortOptions.map((prop) =>
            R.propSatisfies(R.test(RegExp(query, "ig")), prop),
          ),
        ),
        // if a type is selected, filter if the type matches
        R.either(() => !typeFilter, R.propEq("type", typeFilter)),
        // if a tag is selected, filter if this results has the tag
        R.either(
          () => !tagFilter,
          R.compose(R.contains(tagFilter), R.pluck("value"), R.prop("tags")),
        ),
      ]),
    ),
  )(resources);

  // useMemo so this only indexs if the types change
  const typeLookup = useMemo(() => R.indexBy(R.prop("type"), types), [types]);

  return (
    <div className="resource">
      <div className="resources__filters">
        <input
          type="text"
          className="resource__search-text search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
        />

        <Select
          className="resources__tags-select"
          placeholder={`${tagFacetLabel} (${tags.length})`}
          onChange={(item) => setTagFilter(R.prop("value", item))}
          selectProps={{
            itemToString: R.prop("value"),
            items: tags,
          }}
        />
        <Select
          className="resources__types-select"
          placeholder={`${typeFacetLabel} (${types.length})`}
          onChange={(item) => setTypeFilter(R.prop("type", item))}
          selectProps={{
            itemToString: R.prop("type"),
            items: types,
          }}
        />
      </div>
      <div className="resources__results">
        <table>
          <thead>
            <tr>
              <th className="resources__table-header">
                <button
                  className={cx("resources__sort", {
                    "resources__sort--ascending":
                      sortAsc && sortField === "title",
                    "resources__sort--descending":
                      !sortAsc && sortField === "title",
                  })}
                  onClick={() => handleSort("title")}
                  type="button"
                >
                  {titleLabel}

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
              <th className="resources__table-header">
                <button
                  className={cx("resources__sort", {
                    "resources__sort--ascending":
                      sortAsc && sortField === "author",
                    "resources__sort--descending":
                      !sortAsc && sortField === "author",
                  })}
                  onClick={() => handleSort("author")}
                  type="button"
                >
                  {authorLabel}

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
              <th className="resources__table-header">
                <button
                  className={cx("resources__sort", {
                    "resources__sort--ascending":
                      sortAsc && sortField === "year",
                    "resources__sort--descending":
                      !sortAsc && sortField === "year",
                  })}
                  onClick={() => handleSort("year")}
                  type="button"
                >
                  {yearLabel}

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-chevron-down"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </th>
              <th className="resources__table-header">{fileTypeLabel}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => (
              <tr key={result.title}>
                <td>
                  <HighlightText query={query} text={result.title || ""} />
                </td>
                <td>
                  <HighlightText query={query} text={result.author || ""} />
                </td>
                <td>
                  <HighlightText query={query} text={result.year || ""} />
                </td>
                <td className="resources__file-type">
                  <img
                    src={R.prop("fileIconUrl", typeLookup[result.type])}
                    alt={result.type}
                    title={result.type}
                  />
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={result.url}
                    className="resources__link"
                    title={
                      result.type === "Website"
                        ? "Go to website"
                        : `Download ${result.type}`
                    }
                  >
                    <img
                      src={R.prop("linkIconUrl", typeLookup[result.type])}
                      alt=""
                    />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

Resources.propTypes = propTypes;
Resources.defaultProps = defaultProps;
export default Resources;
